import {
  CLOSED_STATUSES,
  GuestLookup,
  GuestStandingNotes,
  Reservation,
  ReservationGuest,
  StandingNotesChange,
} from '../models/reservation';
import { usPhoneDigits } from './us-phone.util';

/**
 * How a guest's history at the restaurant reads on the book (FNF-442 /
 * FNF-1187). reservation-service counts the visits and no-shows and keeps the
 * standing notes; this only words them. Pure, so it is unit-tested.
 */

export interface VisitBadge {
  label: string;
  tone: 'new' | 'returning' | 'regular';
  tooltip: string;
}

/** From this many earlier visits a guest reads as a regular. */
export const REGULAR_FROM_VISITS = 5;

/**
 * The server keeps guest history: staff responses from it always carry
 * `no_shows`. An older reservation-service sends none, and drops standing
 * notes it is given without an error, so the page has to say so itself.
 */
export function historySupported(guest: ReservationGuest): boolean {
  return guest.noShows != null;
}

/** Notes were sent with a save, but the server that answered can't keep them. */
export function standingNotesDropped(
  sent: StandingNotesChange | null | undefined,
  saved: Reservation,
): boolean {
  return !!sent && !historySupported(saved.guest);
}

/** The guest can be recognised next time: there is history to show. */
export function isKnownGuest(guest: ReservationGuest): boolean {
  return !!guest.guestKey;
}

/**
 * Visits before this booking. The count includes this booking once it is
 * seated, and a host greeting the party wants to know about the times before.
 */
export function priorVisits(r: Reservation): number {
  const visits = Math.max(0, Number(r.guest.visits ?? 0));
  return ['seated', 'completed'].includes(r.status) ? Math.max(0, visits - 1) : visits;
}

/** "New", "2nd visit", "5 visits"; null for a guest known by name only. */
export function visitBadge(r: Reservation): VisitBadge | null {
  if (!isKnownGuest(r.guest)) {
    return null;
  }
  const before = priorVisits(r);
  if (before === 0) {
    return { label: 'New', tone: 'new', tooltip: 'First visit here' };
  }
  const plural = before === 1 ? 'visit' : 'visits';
  return {
    label: before === 1 ? '2nd visit' : `${before} visits`,
    tone: before >= REGULAR_FROM_VISITS ? 'regular' : 'returning',
    tooltip: `${before} earlier ${plural} here`,
  };
}

/** The allergy text for the warning chip, or null when none is noted. */
export function allergyWarning(guest: ReservationGuest): string | null {
  return guest.standingNotes?.allergies?.trim() || null;
}

/**
 * The no-show line on the guest card, worded neutrally and about recent
 * bookings only, so one missed booking years ago doesn't follow a regular
 * forever. Null when there is nothing to say: the line is then not shown at
 * all, never as "0 missed". Never shown in lists.
 */
export function noShowLine(guest: ReservationGuest): string | null {
  const missed = Number(guest.recentNoShows ?? 0);
  const of = Number(guest.recentBookings ?? 0);
  if (missed <= 0 || of <= 0) {
    return null;
  }
  return `Missed ${missed} of the last ${of} ${of === 1 ? 'booking' : 'bookings'}`;
}

/**
 * The day's booking already made under this phone number, if any: a "walk-in"
 * who booked should be seated on their booking, not queued a second time.
 * Open bookings only (pending, confirmed, or seated), earliest first.
 */
export function bookingForPhone(
  phone: string | null | undefined,
  bookings: Reservation[],
): Reservation | null {
  const digits = usPhoneDigits(phone);
  if (digits.length !== 10) {
    return null;
  }
  return (
    bookings
      .filter(
        (r) =>
          r.kind === 'reservation' &&
          !CLOSED_STATUSES.includes(r.status) &&
          usPhoneDigits(r.guest.phone) === digits,
      )
      .sort((a, b) => a.startAt.localeCompare(b.startAt))[0] ?? null
  );
}

/** "2 bookings · 3 visits" for the New reservation greeting. */
export function returningGuestSummary(g: GuestLookup): string {
  const count = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;
  const parts = [];
  if (g.bookings > 0) {
    parts.push(count(g.bookings, 'booking', 'bookings'));
  }
  if (g.visits > 0) {
    parts.push(count(g.visits, 'visit', 'visits'));
  }
  return parts.join(' · ') || 'Has guest notes here';
}

export function hasStandingNotes(notes: GuestStandingNotes | null | undefined): boolean {
  return !!(notes?.allergies || notes?.preferences || notes?.occasions || notes?.tags?.length);
}

/** "regular, VIP , regular" → ["regular", "VIP"]. */
export function parseTags(text: string | null | undefined): string[] {
  return [
    ...new Set(
      String(text ?? '')
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean),
    ),
  ];
}

const NOTE_FIELDS = ['allergies', 'preferences', 'occasions'] as const;

/**
 * Only what the host changed, so a save never overwrites a field someone else
 * edited meanwhile. '' clears a field. Null when nothing changed.
 */
export function standingNotesChange(
  before: GuestStandingNotes | null | undefined,
  after: { allergies?: string; preferences?: string; occasions?: string; tags?: string[] },
): StandingNotesChange | null {
  const change: StandingNotesChange = {};
  for (const field of NOTE_FIELDS) {
    const next = (after[field] ?? '').trim();
    if (next !== (before?.[field] ?? '')) {
      change[field] = next;
    }
  }
  const tags = after.tags ?? [];
  if (tags.join('\u0000') !== (before?.tags ?? []).join('\u0000')) {
    change.tags = tags;
  }
  return Object.keys(change).length ? change : null;
}
